import { useEffect, useRef } from "react";
import { Separator } from "@/frontend/components/ui/separator";
import { FormField } from "@/frontend/components/ui/form-field";
import { SegmentedControl } from "@/frontend/components/ui/segmented-control";
import type { FhirServerFormState } from "@/frontend/types/dashboard/settings/export";

const FHIR_AUTH_TYPE_OPTIONS = [
  { value: "none", label: "None" },
  { value: "oauth2_client_credentials", label: "OAuth2" },
  { value: "bearer_token", label: "Bearer" },
];

export function FhirServerForm({
  state, onChange,
}: {
  state: FhirServerFormState;
  onChange: (s: FhirServerFormState) => void;
}) {
  const update = (field: string, value: string) =>
    onChange({ ...state, [field]: value });

  const authSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (state.authType === "oauth2_client_credentials") {
      authSectionRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, [state.authType]);

  return (
    <>
      <div className="px-6 pb-4 space-y-3">
        <FormField label="Destination Name" placeholder="e.g. staging-hapi"
          value={state.name} onChange={(e) => update("name", e.target.value)} />
        <FormField label="FHIR Base URL" placeholder="https://hapi.fhir.org/baseR4"
          value={state.fhirServerUrl} onChange={(e) => update("fhirServerUrl", e.target.value)} />
      </div>

      <Separator />

      <div ref={authSectionRef} className="px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-foreground/80">Authentication</span>
          <SegmentedControl
            value={state.authType}
            onChange={(v) => update("authType", v)}
            options={FHIR_AUTH_TYPE_OPTIONS}
          />
        </div>

        {state.authType === "oauth2_client_credentials" && (
          <div className="space-y-3">
            <FormField
              label="Token Endpoint"
              placeholder="https://login.example.com/oauth2/token"
              value={state.tokenEndpoint}
              onChange={(e) => update("tokenEndpoint", e.target.value)}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                label="Client ID"
                placeholder="client_id"
                value={state.clientId}
                onChange={(e) => update("clientId", e.target.value)}
              />
              <FormField
                label="Client Secret"
                placeholder="••••••••"
                type="password"
                value={state.clientSecret}
                onChange={(e) => update("clientSecret", e.target.value)}
              />
            </div>
            <FormField
              label="Scope"
              placeholder="e.g. system/*.write"
              optional
              value={state.scope}
              onChange={(e) => update("scope", e.target.value)}
            />
          </div>
        )}

        {state.authType === "bearer_token" && (
          <FormField label="Bearer Token" placeholder="eyJhbGciOi..."
            value={state.bearerToken} onChange={(e) => update("bearerToken", e.target.value)} />
        )}
        </div>
    </>
  );
}
